import type React from "react";
import {
  ActionIcon,
  Center,
  Divider,
  Group,
  HoverCard,
  Loader,
  Stack,
  Text,
} from "@mantine/core";
import { Badge } from "@xplatform/design-system";
import { IconInfoCircle } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import type { SimilarHistoryEntry } from "@models/job";
import { similarityColor } from "@utils/similarity";

const pct = (sim: number): number => Math.round(sim * 100);

interface SimilarExamplesHoverCardProps {
  /** Examples to list. `null` means "not loaded yet". */
  entries: SimilarHistoryEntry[] | null;
  loading?: boolean;
  /** Fired when the card opens — used by callers that fetch lazily. */
  onOpen?: () => void;
}

/**
 * Info icon that reveals, on HOVER, the past confirmations behind a score —
 * "you accepted a similar example before": the source value, what it was
 * mapped to, who confirmed it and how close it was.
 *
 * Purely presentational: TM feeds it examples fetched on first open, wd feeds
 * it the ones already stamped on the match metadata.
 */
const SimilarExamplesHoverCard: React.FC<SimilarExamplesHoverCardProps> = ({
  entries,
  loading = false,
  onOpen,
}) => {
  const { t } = useTranslation();

  return (
    <HoverCard
      width={380}
      shadow="md"
      position="left-start"
      openDelay={100}
      closeDelay={80}
      withinPortal
      onOpen={onOpen}
    >
      <HoverCard.Target>
        <ActionIcon
          size="xs"
          variant="subtle"
          color="gray"
          aria-label={t("jobResultsPage.score.examplesAria", {
            defaultValue: "See similar accepted examples",
          })}
          onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          <IconInfoCircle size={14} />
        </ActionIcon>
      </HoverCard.Target>
      <HoverCard.Dropdown>
        <Stack gap={8}>
          <Text
            size="xs"
            fw={700}
            tt="uppercase"
            c="green"
            style={{ letterSpacing: "0.04em" }}
          >
            {t("jobResultsPage.score.acceptedBefore", {
              defaultValue: "You accepted the similar example before",
            })}
          </Text>
          {loading && (
            <Center py={8}>
              <Loader size="xs" />
            </Center>
          )}
          {!loading && entries !== null && entries.length === 0 && (
            <Text size="sm" c="dimmed">
              {t("jobResultsPage.score.noExamples", {
                defaultValue: "No similar examples",
              })}
            </Text>
          )}
          {!loading &&
            entries?.map((ex, i) => (
              <div key={i}>
                {i > 0 && <Divider my={4} />}
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                  <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
                    <Text size="sm" c="blue" lineClamp={2}>
                      {ex.source?.value ?? "-"}
                    </Text>
                    {ex.source?.path && (
                      <Text size="xs" c="dimmed" ff="monospace" lineClamp={1}>
                        {ex.source.path}
                      </Text>
                    )}
                    <Text size="sm" lineClamp={2}>
                      {"→ "}
                      {ex.mapped?.item_mp_text ?? ex.mapped?.item_mp_id ?? "-"}
                      {ex.mapped?.value_display
                        ? ` · ${ex.mapped.value_display}`
                        : ""}
                    </Text>
                    {ex.accepted_by && (
                      <Text size="xs" c="dimmed">
                        {t("jobResultsPage.score.acceptedBy", {
                          defaultValue: "by {{who}}",
                          who: ex.accepted_by,
                        })}
                        {ex.accepted_at
                          ? ` · ${ex.accepted_at.slice(0, 10)}`
                          : ""}
                      </Text>
                    )}
                  </Stack>
                  <Badge
                    size="sm"
                    variant="light"
                    color={similarityColor(pct(ex.similarity))}
                  >
                    {pct(ex.similarity)}%
                  </Badge>
                </Group>
              </div>
            ))}
        </Stack>
      </HoverCard.Dropdown>
    </HoverCard>
  );
};

export default SimilarExamplesHoverCard;
